import { memo, useRef, useState } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ARIA_ACCEPT_ATTR } from '@/lib/ariaAcceptedFiles';
import { extractDroppedFiles } from '@/lib/readDroppedEntries';

/**
 * Composer del chat IA. Posee su PROPIO estado de texto y solo lo sube al enviar
 * (`onSend(text)`), de modo que teclear NO re-renderiza el AIPanel ni la lista de
 * mensajes (fix del lag de escritura). Memoizado: solo re-renderiza si cambian sus
 * props, nunca por una pulsación.
 *
 * Nota: hoy nadie precarga el input desde fuera (las sugerencias y el pendingPrompt
 * llaman a `send()` directo). Si en el futuro se necesita sembrar texto, añadir una
 * prop controlada opcional `seedText`.
 *
 * ## Adjuntar ficheros
 *
 * El clip y la zona de soltar aparecen **solo si el padre pasa `onAddFiles`**, el
 * mismo patrón que usa `InputBar` (la capacidad se activa por la presencia del
 * callback). Así el chat del Portal del Asesor —el otro consumidor— no se entera
 * de que existen: no los pasa y sigue siendo un composer de solo texto.
 *
 * Se añadió porque el system prompt le promete el clip 📎 a ARIA en TODAS las
 * superficies, y el panel global —el que se usa en toda la app— no lo tenía: ARIA
 * te pedía adjuntar un fichero que no había forma de adjuntar.
 */
function AIComposer({
  onSend, disabled = false, placeholder,
  onAddFiles, attachmentsSlot,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const supportsUpload = typeof onAddFiles === 'function';

  const submit = (e) => {
    e.preventDefault();
    const v = text.trim();
    // Con adjuntos se puede enviar SIN texto: el store redacta el mensaje por
    // canal ("He adjuntado un fichero para importar…"), así que obligar a escribir
    // algo sería pedir copy que ya existe.
    if (disabled || (!v && !supportsUpload)) return;
    onSend(v);
    setText('');
  };

  const pick = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) onAddFiles(files);
  };

  const onDrop = async (e) => {
    if (!supportsUpload || disabled) return;
    e.preventDefault();
    setDragging(false);
    // `extractDroppedFiles` recorre carpetas si el navegador expone entries. El
    // filtrado (formato y tamaño) lo hace el padre con los predicados reales.
    pick(await extractDroppedFiles(e.dataTransfer));
  };

  return (
    <div className="border-t border-border">
      {attachmentsSlot}
      <form
        className={`p-3 flex items-center gap-2 ${dragging ? 'bg-primary/5 ring-1 ring-inset ring-primary/40' : ''}`}
        onSubmit={submit}
        onDragOver={(e) => { if (supportsUpload && Array.from(e.dataTransfer?.types || []).includes('Files')) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {supportsUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              // MIME y extensiones: con solo MIME el diálogo del sistema oculta
              // los ficheros que el SO no sabe etiquetar (un `.csv`, por ejemplo).
              accept={ARIA_ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => { pick(e.target.files); e.target.value = ''; }}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="w-9 h-9 shrink-0"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
              aria-label={t('ai.attach_document', { defaultValue: 'Adjuntar documento' })}
              title={t('ai.attach_document', { defaultValue: 'Adjuntar documento' })}
              data-testid="ai-panel-attach"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
          </>
        )}
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="flex-1 h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label={placeholder}
          data-testid="ai-panel-input"
        />
        <Button type="submit" size="icon" className="w-9 h-9" disabled={disabled || (!text.trim() && !supportsUpload)}>
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

export default memo(AIComposer);
